import React, { useEffect, useState } from 'react';
import { tvApi, movieApi } from 'api';
import styled from 'styled-components';
import { Loader } from 'Components/Loader';
import { Section } from 'Components/Section';
import { Poster } from 'Components/Poster';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import useInfiniteScroll from 'useInfiniteScroll';
import uniqBy from 'lodash.uniqby';
import { Helmet } from 'react-helmet';

const Form = styled.form`
  display: flex;
  justify-content: center;
  height: 70px;
  @media (max-width: 768px) {
    height: 60px;
  }
`;

const Input = styled.input`
  all: unset;
  width: 75%;
  max-width: 460px;
  font-size: 18px;
  color: white;
  border: solid 5px #e50914;
  border-top-left-radius: 15px;
  border-bottom-left-radius: 15px;
  border-right: none;
  padding-left: 1.5%;
  @media (max-width: 768px) {
    font-size: 14px;
    padding-left: 5%;
  }
`;

const Button = styled.input`
  width: 55px;
  background-color: #e50914;
  color: white;
  border: none;
  border-top-right-radius: 15px;
  border-bottom-right-radius: 15px;
  font-size: 18px;
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const HeaderContainer = styled.div`
  width: 100%;
  height: 100%;
  font-weight: 600;
  font-size: 18px;
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const Ul = styled.ul`
  display: flex;
  height: 100px;
  justify-content: center;
  align-items: center;
`;

const Li = styled.li`
  margin: 15px;
  color: ${props => (props.current ? 'red' : 'white')};
  opacity: ${props => (props.current ? '1' : '0.8')};
  transition: all 0.2s ease-in-out;
  &:hover {
    transform: scale(1.1);
    opacity: 1;
  }
`;

const TitleContainer = styled.div`
  margin-top: 125px;
`;

const Title = styled.div`
  text-align: center;
  font-size: 26px;
  margin-bottom: 30px;
  font-weight: 800;
  opacity: 0.6;
  line-height: 1.5;
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const Container = styled.div`
  // 검색 결과
  margin-top: 50px;
`;

const Search = ({ location: { pathname } }) => {
  const [movieResults, setMovieResults] = useState([]);
  const [tvResults, setTvResults] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const page = useInfiniteScroll();

  const handleSubmit = event => {
    event.preventDefault();
    if (searchTerm !== '') {
      searchByTerm();
    }
  };

  const updateTerm = event => {
    const {
      target: { value }
    } = event;
    setSearchTerm(value);
  };

  const searchByTerm = async () => {
    setLoading(true);
    try {
      const {
        data: { results: movieResults }
      } = await movieApi.search(searchTerm);
      setMovieResults(movieResults);
      const {
        data: { results: tvResults }
      } = await tvApi.search(searchTerm);
      setTvResults(tvResults);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const searchByInfiniteTerm = async () => {
    if (page !== 1)
      if (pathname === '/search') {
        try {
          const {
            data: { results: newTvResults }
          } = await tvApi.searchInfinite(searchTerm, page);
          const totalTvs = [...tvResults, ...newTvResults];
          const uniqByTvs = uniqBy(totalTvs, 'id');
          setTvResults(uniqByTvs);
        } catch (e) {
          console.log(e);
        }
      } else if (pathname === '/search/movie') {
        try {
          const {
            data: { results: newMovieResults }
          } = await movieApi.searchInfinite(searchTerm, page);
          const totalMovies = [...movieResults, ...newMovieResults];
          const uniqByMovies = uniqBy(totalMovies, 'id');
          setMovieResults(uniqByMovies);
        } catch (e) {
          console.log(e);
        }
      }
  };

  useEffect(() => {
    searchByInfiniteTerm();
  }, [page]);

  return (
    <>
      <Helmet>
        <title>검색 - ScFlix</title>
      </Helmet>
      <TitleContainer>
        <Title>수백만 편의 영화, TV 프로그램이 있습니다.</Title>
        <Title>지금 바로 검색해보세요.</Title>
      </TitleContainer>
      <Form onSubmit={handleSubmit}>
        <Input
          type="text"
          placeholder="영화 또는 TV 프로그램을 검색하세요."
          value={searchTerm}
          onChange={updateTerm}
        />
        <Button type="submit" value="검색" />
      </Form>
      <HeaderContainer>
        <Ul>
          <Li current={pathname === '/search'}>
            <Link to="/search">TV 프로그램</Link>
          </Li>
          <Li current={pathname === '/search/movie'}>
            <Link to="/search/movie">영화</Link>
          </Li>
        </Ul>
      </HeaderContainer>
      {loading ? (
        <Loader />
      ) : (
        <Container>
          {pathname === '/search/movie' &&
            movieResults &&
            movieResults.length > 0 && (
              <Section>
                {movieResults.map(movie => (
                  <Poster
                    key={movie.id}
                    id={movie.id}
                    title={movie.title ? movie.title : movie.original_title}
                    posterUrl={movie.poster_path}
                    rating={movie.vote_average}
                    year={
                      movie.release_date && movie.release_date.substring(0, 4)
                    }
                    isMovie={true}
                  />
                ))}
              </Section>
            )}
          {pathname === '/search' && tvResults && tvResults.length > 0 && (
            <Section>
              {tvResults.map(tv => (
                <Poster
                  key={tv.id}
                  id={tv.id}
                  title={tv.name ? tv.name : tv.original_name}
                  posterUrl={tv.poster_path}
                  rating={tv.vote_average}
                  year={tv.first_air_date && tv.first_air_date.substring(0, 4)}
                  isMovie={false}
                />
              ))}
            </Section>
          )}
        </Container>
      )}
    </>
  );
};

export default withRouter(Search);
